import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import CustomDropdown from '../components/CustomDropdown';
import { toLocalDateString } from '../utils/localDate';

const PERIODS = [
  { months: 1, label: '1개월' },
  { months: 3, label: '3개월' },
  { months: 6, label: '6개월' },
];

/**
 * 이행률에 따라 막대 색을 고른다. 잘된 것과 못된 것이 한눈에 갈려야 한다.
 *
 * 위쪽 '미해결 경과 기간' 칸과 같은 계열의 옅은 색을 쓴다. 한 화면에서 위는
 * 파스텔인데 아래만 진하면 아래쪽이 경고처럼 튀어 보인다.
 */
const rateColor = (rate) => {
  if (rate >= 95) return '#6ee7b7';
  if (rate >= 80) return '#fcd34d';
  return '#fca5a5';
};

const STAT_LINK_CSS = `
  .stat-link { transition: background-color 0.15s ease; }
  .stat-link:hover { background-color: #eef2ff; }
`;

const days = (n) => (n == null ? '-' : n === 0 ? '당일' : `${n}일`);

const BUCKET_STYLE = {
  '7일 미만': { bg: '#d1fae5', color: '#065f46' },
  '7~30일': { bg: '#fef3c7', color: '#92400e' },
  '30일 이상': { bg: '#fee2e2', color: '#991b1b' },
};

const StatisticsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const months = Number(searchParams.get('months')) || 3;
  const [selectedFacilityId, setSelectedFacilityId] = useState(
    searchParams.get('facilityId') || 'all',
  );

  const [facilities, setFacilities] = useState([]);
  const [issueStats, setIssueStats] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const to = new Date();
      const from = new Date();
      from.setMonth(from.getMonth() - months);

      const range = { from: toLocalDateString(from), to: toLocalDateString(to) };
      const facilityParam =
        selectedFacilityId !== 'all' ? { facilityId: selectedFacilityId } : {};

      const [facRes, issueRes, breakdownRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/issues/stats', { params: { ...range, ...facilityParam } }),
        api.get('/inspection-plans/slots/breakdown', {
          params: { ...range, ...facilityParam },
        }),
      ]);

      setFacilities(facRes.data);
      setIssueStats(issueRes.data);
      setBreakdown(breakdownRes.data);
    } catch (error) {
      console.error('Failed to load statistics', error);
    } finally {
      setLoading(false);
    }
  }, [months, selectedFacilityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setPeriod = (m) =>
    setSearchParams({ months: String(m), facilityId: selectedFacilityId });

  const handleFacilityChange = (value) => {
    setSelectedFacilityId(value);
    setSearchParams({ months: String(months), facilityId: value });
  };

  const openBuckets = issueStats?.open?.buckets || [];
  const bucketTotal = openBuckets.reduce((sum, b) => sum + b.count, 0);

  const renderRateRow = (row) => (
    <div key={row.id} className="d-flex align-items-center gap-2 py-1">
      {/* 이름 칸을 고정 폭으로 두면 좁은 화면에서 막대가 설 자리를 잃는다.
          줄어들되 너무 좁아지지는 않게 둔다. */}
      <span
        className="small text-truncate"
        style={{ flex: '0 1 130px', minWidth: '72px' }}
        title={row.name}
      >
        {row.name}
      </span>
      <div
        className="flex-grow-1 rounded-pill"
        style={{ height: '8px', background: '#f3f4f6', overflow: 'hidden' }}
      >
        <div
          style={{
            width: `${row.completionRate}%`,
            height: '100%',
            background: rateColor(row.completionRate),
          }}
        />
      </div>
      <span
        className="text-muted text-nowrap"
        style={{ textAlign: 'right', fontSize: '0.75rem' }}
      >
        {row.completionRate}%
        <span className="d-none d-sm-inline">
          {' · '}
          {row.completed}/{row.required}
        </span>
      </span>
    </div>
  );

  return (
    <div className="pb-5">
      <style>{STAT_LINK_CSS}</style>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h4 className="fw-bold m-0">
          <i className="bi bi-bar-chart-fill text-primary me-2"></i>통계
        </h4>
        <CustomDropdown
          options={[
            { value: 'all', label: '전체 시설' },
            ...facilities.map((f) => ({ value: String(f.id), label: f.name })),
          ]}
          value={String(selectedFacilityId)}
          onChange={handleFacilityChange}
          className="shadow-sm rounded-pill fw-bold"
          style={{ minWidth: '140px', maxWidth: '180px', backgroundColor: '#fdfdfd' }}
        />
      </div>

      <div className="btn-group mb-4" role="group">
        {PERIODS.map((p) => (
          <button
            key={p.months}
            className={`btn btn-sm ${months === p.months ? 'btn-primary' : 'btn-white border'}`}
            onClick={() => setPeriod(p.months)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-4 border p-3 mb-3">
            <h6 className="fw-bold mb-3">이슈 처리</h6>

            <div className="row g-2 mb-3">
              {[
                // 평균이 아니라 중앙값이다. 오래 끈 한 건에 끌려가면 실제
                // 체감과 멀어진다.
                // 0일은 값이 없는 것처럼 읽힌다. 실제로는 대부분 당일에 끝난다는
                // 뜻이라, 숫자 대신 그 말을 쓴다.
                { label: '해결까지 보통', value: days(issueStats?.resolved?.medianDays) },
                { label: '해결까지 최장', value: days(issueStats?.resolved?.maxDays) },
                {
                  // 대시보드도 같은 것을 '미해결 이슈' 라고 부른다. 화면마다
                  // 다르게 부르면 같은 것인지 알 수 없다.
                  label: '미해결',
                  value: `${issueStats?.open?.count ?? 0}건`,
                  danger: (issueStats?.open?.count ?? 0) > 0,
                  // 숫자만 보여주고 끝내지 않는다. 누르면 그 목록으로 간다.
                  linkState: { facilityId: selectedFacilityId, resolved: false },
                },
              ].map((card) => {
                const body = (
                  // 라벨이 두 줄이 되면 그 칸 숫자만 아래로 밀려 셋의 높이가
                  // 어긋난다. 라벨은 위, 숫자는 아래에 고정해 어느 쪽이 길어져도
                  // 숫자 줄이 나란히 서게 한다.
                  <div
                    className={`bg-light rounded-3 p-2 h-100 text-center d-flex flex-column justify-content-between ${card.linkState ? 'stat-link' : ''}`}
                  >
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {card.label}
                      {card.linkState && <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.6rem' }}></i>}
                    </div>
                    <div className={`fw-bold ${card.danger ? 'text-danger' : ''}`} style={{ fontSize: '1.35rem' }}>
                      {card.value}
                    </div>
                  </div>
                );
                return (
                  <div key={card.label} className="col-4">
                    {card.linkState ? (
                      <Link to="/reports" state={card.linkState} className="text-decoration-none text-dark d-block h-100">
                        {body}
                      </Link>
                    ) : body}
                  </div>
                );
              })}
            </div>

            {bucketTotal > 0 && (
              <>
                <div className="text-muted small mb-1">미해결 경과 기간</div>
                <div className="d-flex mb-3" style={{ gap: '2px' }}>
                  {openBuckets
                    .filter((b) => b.count > 0)
                    .map((b) => {
                      const style = BUCKET_STYLE[b.label] || {};
                      return (
                        <div
                          key={b.label}
                          className="px-2 py-1 text-nowrap"
                          style={{
                            flex: b.count,
                            background: style.bg,
                            color: style.color,
                            fontSize: '0.72rem',
                            borderRadius: '4px',
                          }}
                        >
                          {b.label} {b.count}
                        </div>
                      );
                    })}
                </div>
              </>
            )}

            {issueStats?.open?.oldest?.length > 0 && (
              <>
                {/* 숫자는 문제를 알려주고, 이름은 손댈 곳을 알려준다. */}
                <div className="text-muted small mb-1">가장 오래된 미해결 이슈</div>
                {issueStats.open.oldest.map((issue) => (
                  <div
                    key={issue.id}
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/issues/${issue.id}`)}
                  >
                    <span className="small flex-grow-1 text-truncate">{issue.title}</span>
                    <span className="text-muted text-nowrap" style={{ fontSize: '0.72rem' }}>
                      {issue.facility}
                    </span>
                    <span className="fw-bold text-danger text-nowrap" style={{ fontSize: '0.8rem' }}>
                      {issue.days}일
                    </span>
                  </div>
                ))}

                {/* 오래된 다섯 건만 보여주므로, 나머지가 있으면 그리로 가는 길을
                    둔다. 읽다가 끊긴 자리에 이어갈 데가 없으면 막힌다. */}
                {issueStats.open.count > issueStats.open.oldest.length && (
                  <Link
                    to="/reports"
                    state={{ facilityId: selectedFacilityId, resolved: false }}
                    className="d-block text-center text-decoration-none small fw-bold text-primary pt-2"
                  >
                    미해결 {issueStats.open.count}건 모두 보기
                    <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                  </Link>
                )}
              </>
            )}
          </div>

          <div className="bg-white rounded-4 border p-3">
            <h6 className="fw-bold mb-1">점검 이행률</h6>
            <p className="text-muted small mb-3">약한 곳부터</p>

            {(breakdown?.byPlan?.length ?? 0) === 0 ? (
              <p className="text-muted small mb-0">이 기간에 마감된 점검이 없습니다.</p>
            ) : (
              <>
                <div className="text-muted small mb-1">계획별</div>
                <div className="mb-3">{breakdown.byPlan.map(renderRateRow)}</div>

                <div className="text-muted small mb-1">시설별</div>
                <div>{breakdown.byFacility.map(renderRateRow)}</div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StatisticsPage;
